import { Box, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import { Link } from "react-router";
import { ConfusionMatrixEntry } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  formatCell,
  getCellColor,
  identityFormatLabel,
} from "./confusionMatrixUtils";

interface Props {
  entries: ConfusionMatrixEntry[];
  formatLabel?: (label: string) => string;
  selectedCell: { predicted: string; actual: string } | null;
  buildCellHref: (predicted: string, actual: string) => string;
}

export const AccuracyMatrix = ({
  entries,
  formatLabel = identityFormatLabel,
  selectedCell,
  buildCellHref,
}: Props) => {
  const { t } = useTenantTranslation();
  const formatPercent = useFormatNumber()(1, true);

  const { predictedLabels, actualLabels, countMap, actualTotals } =
    useMemo(() => {
      const { predicted, actual, counts, totals } = entries.reduce(
        (acc, entry) => {
          acc.predicted.add(entry.predicted);
          acc.actual.add(entry.actual);
          acc.counts.set(`${entry.predicted}|${entry.actual}`, entry.count);
          acc.totals.set(
            entry.actual,
            (acc.totals.get(entry.actual) ?? 0) + entry.count
          );
          return acc;
        },
        {
          predicted: new Set<string>(),
          actual: new Set<string>(),
          counts: new Map<string, number>(),
          totals: new Map<string, number>(),
        }
      );
      return {
        predictedLabels: [...predicted].sort(),
        actualLabels: [...actual].sort(),
        countMap: counts,
        actualTotals: totals,
      };
    }, [entries]);

  if (entries.length === 0) {
    return (
      <Typography color="text.secondary">
        {t("noConfusionMatrixData")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `auto ${actualLabels.map(() => "1fr").join(" ")}`,
        gap: "2px",
        maxWidth: 800,
        "& > *": {
          px: 1.5,
          py: 1,
          whiteSpace: "nowrap",
          textAlign: "center",
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: "bold", textAlign: "left" }}
      >
        {t("predicted")} \ {t("actual")}
      </Typography>
      {actualLabels.map((label) => (
        <Typography key={label} variant="body2" sx={{ fontWeight: "bold" }}>
          {formatLabel(label)}
        </Typography>
      ))}
      {predictedLabels.map((predicted) => (
        <Box key={predicted} sx={{ display: "contents" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", textAlign: "left" }}
          >
            {formatLabel(predicted)}
          </Typography>
          {actualLabels.map((actual) => {
            const count = countMap.get(`${predicted}|${actual}`) ?? 0;
            const total = actualTotals.get(actual) ?? 0;
            const isErrorRate = predicted !== actual;
            const isSelected =
              selectedCell?.predicted === predicted &&
              selectedCell.actual === actual;
            return (
              <Box
                key={actual}
                component={Link}
                to={buildCellHref(predicted, actual)}
                sx={{
                  textDecoration: "none",
                  borderRadius: 1,
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? "primary.main" : "divider",
                  cursor: "pointer",
                  transition: "background-color 120ms",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isErrorRate ? "normal" : "bold",
                    color: getCellColor({
                      isErrorRate,
                      percent: (count / total) * 100,
                      background: "light",
                    }),
                  }}
                >
                  {formatCell(count, total, formatPercent)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};
